import { Plus, X } from 'lucide-react';
import { useState } from 'react';
import { jourCourt, type DateIso } from './dates';
import { FOCUS_ATTR, PASTILLE_ATTR, pastilleId } from './constantes';
import { couleurDuPole, PastilleSeance } from './PastilleSeance';
import type { Seance } from './seancesDemande';
import { TexteDiffere } from './TexteDiffere';
import { dePersonne } from './textesDemande';
import type { Nd } from './useNouvelleDemande';

interface Props {
  nd: Nd;
  date: DateIso;
  ouverte: string | null;
  onOuvrirSeance: (s: Seance, ou: 'c' | 'p') => void;
}

/** Un jour choisi dans le panneau : ses séances (mêmes pastilles que le calendrier), ajouter un autre atelier, noter une autre activité. */
export function JourDuPanneau({ nd, date, ouverte, onOuvrirSeance }: Props) {
  const { etat, dispatch, seancesDuJour, choixEtat, personnesParId, meta, ajoutablesDe, pourMoi, personneAbsente, programmeDuJour } = nd;
  const ss = seancesDuJour(date);
  const [choisirAtelier, setChoisirAtelier] = useState(false);
  const ajoutables = ajoutablesDe(date);
  const noteVisible = etat.noteOuverte === date || !!etat.notesJour[date];
  const prenom = personneAbsente?.prenom ?? '';

  return (
    <div className="nd-jour-panneau" role="group" data-jour-panneau={date} tabIndex={-1} aria-label={jourCourt(date)}>
      <div className="nd-jour-t">{jourCourt(date)}</div>
      {ss.length === 0 && etat.absentId != null && (
        <span className="nd-vide">{{ ok: "Pas d'atelier ce jour-là", chargement: 'Chargement des séances…', erreur: 'Séances indisponibles' }[programmeDuJour(date)]}</span>
      )}
      {ss.map((s) => (
        <div className="nd-jour-ligne" key={s.cle}>
          <PastilleSeance seance={s} e={choixEtat} personnes={personnesParId} couleurPole={couleurDuPole(meta, s.poleId)} ou="p" ouverte={ouverte === s.cle} onOuvrir={onOuvrirSeance} />
          {s.ajoutee && (
            <button
              type="button" className="btn btn-icon btn-sm" title="Retirer cet atelier de la demande" aria-label={`Retirer ${s.atelierNom} de la demande`}
              onClick={() => {
                dispatch({ type: 'retirerAtelier', cle: s.cle });
                dispatch({ type: 'message', texte: `${s.atelierNom} retiré du ${jourCourt(date)} : il ne fait plus partie de la demande.` });
                nd.apresRendu(() => document.querySelector<HTMLElement>(`[${FOCUS_ATTR}="at${date}"]`)?.focus());
              }}
            >
              <X size={14} aria-hidden="true" />
            </button>
          )}
        </div>
      ))}

      {/* Un autre atelier de la personne, exceptionnellement ce jour-là (horaire exceptionnel, programme incomplet) : une séance « À rechercher » comme les autres. */}
      {etat.absentId != null && ajoutables.length > 0 && (choisirAtelier ? (
        <div className="nd-ajout-atelier">
          <label className="nd-sr" htmlFor={`nd-at-${date}`}>Un autre atelier le {jourCourt(date)}</label>
          <select
            id={`nd-at-${date}`} className="form-select" defaultValue="" autoFocus
            onChange={(e) => {
              if (!e.target.value) return;
              const cle = `${e.target.value}_${date}`;
              const nom = ajoutables.find((a) => String(a.id) === e.target.value)?.nom ?? 'Atelier';
              dispatch({ type: 'ajouterAtelier', cle });
              dispatch({ type: 'message', texte: `${nom} ajouté le ${jourCourt(date)} : une séance « À rechercher » de plus.` });
              setChoisirAtelier(false);
              nd.apresRendu(() => document.querySelector<HTMLElement>(`[${PASTILLE_ATTR}="${pastilleId('p', cle)}"]`)?.focus());
            }}
            onBlur={() => setChoisirAtelier(false)}
            onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); setChoisirAtelier(false); } }}
          >
            <option value="">{pourMoi ? 'Choisir un de vos ateliers…' : `Choisir un atelier ${dePersonne(prenom)}…`}</option>
            {ajoutables.map((a) => <option key={a.id} value={a.id}>{a.nom}{a.lieu ? ` · ${a.lieu}` : ''}</option>)}
          </select>
        </div>
      ) : (
        <button
          type="button" className="nd-lien" {...{ [FOCUS_ATTR]: `at${date}` }} onClick={() => setChoisirAtelier(true)}
          aria-label={`${pourMoi ? 'Un autre de vos ateliers ce jour-là' : `Un autre atelier ${dePersonne(prenom)} ce jour-là`}, ${jourCourt(date)}`}
        >
          <Plus size={14} aria-hidden="true" />{pourMoi ? 'Un autre de vos ateliers ce jour-là' : `Un autre atelier ${dePersonne(prenom)} ce jour-là`}
        </button>
      ))}

      {/* Autre activité ce jour-là : réunion, formation, bilan, RDV… Jamais une demande : le texte part dans les précisions (message pour les collègues). */}
      {noteVisible ? (
        <>
          <label className="nd-sr" htmlFor={`nd-nj-${date}`}>Autre activité le {jourCourt(date)} (réunion, formation, bilan, RDV…)</label>
          <TexteDiffere
            id={`nd-nj-${date}`} className="form-input nd-nj" {...{ [FOCUS_ATTR]: `nj${date}` }} aria-describedby={`nd-nj-aide-${date}`} autoFocus={etat.noteOuverte === date}
            placeholder="Autre activité : réunion, formation, bilan, RDV…" valeur={etat.notesJour[date] ?? ''}
            onValider={(texte) => dispatch({ type: 'noteJour', date, texte })}
            onSortie={(texte) => { if (!texte.trim()) dispatch({ type: 'noteOuverte', date: null }); }}
          />
          <p className="nd-aide nd-nj-aide" id={`nd-nj-aide-${date}`}>
            {nd.dire(
              "Ajouté aux précisions de votre demande, que vos collègues lisent dans le message Slack (et des personnes extérieures, si l'équipe gestion élargit la recherche) ; aucun·e remplaçant·e n'est recherché·e pour cela. N'y indiquez aucune information médicale ou personnelle.",
              "Ajouté aux précisions de la demande, que les collègues lisent dans le message Slack (et des personnes extérieures, si l'équipe gestion élargit la recherche) ; aucun·e remplaçant·e n'est recherché·e pour cela. N'y indiquez aucune information médicale ou personnelle.",
            )}
          </p>
        </>
      ) : (
        <button
          type="button" className="nd-lien" {...{ [FOCUS_ATTR]: `on${date}` }} onClick={() => dispatch({ type: 'noteOuverte', date })}
          aria-label={`Autre activité ce jour-là (réunion, formation, bilan, RDV…), ${jourCourt(date)}`}
        >
          <Plus size={14} aria-hidden="true" />Autre activité ce jour-là (réunion, formation, bilan, RDV…)
        </button>
      )}
    </div>
  );
}
